import type { GateId } from "./types";

export const GATE_ORDER: GateId[] = ["meaning", "misread", "context", "recall"];

/** Buff stacks a gate grants; accumulating them to `requirement` "breaks" the gate. */
export type BuffId = "ming" | "po" | "jian" | "fan";

export interface GateDef {
  id: GateId;
  label: string;
  glyph: string; // seal-script hero character on the card
  cardName: string; // faithful card title (時習之義 …)
  tone: "jade" | "vermilion" | "azure" | "gold";
  prompt: string; // attack banner
  description: string;
  reward: string;
  mana: number; // 明點 cost
  damage: number; // 氣勢/破解 flavour number shown on the card + damage float
  buff: BuffId; // stack granted on a correct answer
  buffLabel: string; // 明義 / 破誤 / 踐履 / 反思
  requirement: number; // stacks needed to break this gate
  effect: string; // card effect line
  condition?: string; // conditional bonus line (resolved in combat.ts)
}

export const GATES: GateDef[] = [
  {
    id: "meaning",
    label: "通文",
    glyph: "通",
    cardName: "通文入義",
    tone: "jade",
    prompt: "通文",
    description: "字義、句意、說話的場合，先讀對再說。",
    reward: "明義",
    mana: 2,
    damage: 18,
    buff: "ming",
    buffLabel: "明義",
    requirement: 1,
    effect: "命中 +1 明義",
  },
  {
    id: "misread",
    label: "辨惑",
    glyph: "辨",
    cardName: "辨惑立論",
    tone: "vermilion",
    prompt: "辨惑",
    description: "認出似是而非的解法，說得出它錯在哪。",
    reward: "破誤",
    mana: 3,
    damage: 24,
    buff: "po",
    buffLabel: "破誤",
    requirement: 2,
    effect: "命中 +1 破誤",
    condition: "誤解>60% 再+1",
  },
  {
    id: "context",
    label: "致用",
    glyph: "用",
    cardName: "致用踐履",
    tone: "azure",
    prompt: "致用",
    description: "放進你真會遇到的處境，做出取捨。",
    reward: "踐履",
    mana: 2,
    damage: 16,
    buff: "jian",
    buffLabel: "踐履",
    requirement: 2,
    effect: "命中 +1 踐履",
    condition: "得護盾 16",
  },
  {
    id: "recall",
    label: "通貫",
    glyph: "貫",
    cardName: "通貫諸章",
    tone: "gold",
    prompt: "通貫",
    description: "把這一章接回概念、人物與別章。",
    reward: "反思",
    mana: 3,
    damage: 20,
    buff: "fan",
    buffLabel: "反思",
    requirement: 2,
    effect: "命中 +1 反思",
    condition: "誤解<40% 沉默",
  },
];

export const BUFFS: Array<{ id: BuffId; label: string; gate: GateId }> = GATES.map((g) => ({
  id: g.buff,
  label: g.buffLabel,
  gate: g.id,
}));

const GATE_MAP = new Map(GATES.map((gate) => [gate.id, gate]));
const BUFF_GATE = new Map(GATES.map((gate) => [gate.buff, gate.id]));

export function gateById(id: GateId): GateDef {
  const gate = GATE_MAP.get(id);
  if (!gate) throw new Error(`unknown gate: ${id}`);
  return gate;
}

export function gateLabel(id: GateId): string {
  return gateById(id).label;
}

export function gateByBuff(buff: BuffId): GateId {
  return BUFF_GATE.get(buff)!;
}

/** Total stacks required to fully break a quote — drives 誤解度 (resistance). */
export const TOTAL_REQUIREMENT = GATES.reduce((sum, gate) => sum + gate.requirement, 0);
